import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { CheckCircle, Clock, AlertTriangle, Users, Clipboard, Target } from 'lucide-react';
import { formatGameDateTime } from '@/utils/timezone';

interface CoachDashboardProps {
  gameData: any;
  formData: any;
  userTeamInfo: {
    teamId?: number;
    teamSide?: 'home' | 'away';
  };
  onSectionFocus: (sectionId: string) => void;
}

export const CoachDashboard: React.FC<CoachDashboardProps> = ({
  gameData,
  formData,
  userTeamInfo,
  onSectionFocus
}) => {
  const getCoachTasks = () => {
    const tasks = [
      {
        id: 'coach-roster',
        title: 'Team Roster Management',
        description: 'Review and confirm your team roster',
        status: formData.formSectionsCompleted?.teams ? 'completed' : 'pending',
        priority: 'high',
        section: 'coach'
      },
      {
        id: 'player-status',
        title: 'Player Attendance Review',
        description: 'Check which players have confirmed attendance',
        status: formData.formSectionsCompleted?.players ? 'completed' : 'pending',
        priority: 'high',
        section: 'player'
      },
      {
        id: 'team-strategy',
        title: 'Game Strategy Notes',
        description: 'Add strategy notes and game plan',
        status: formData.strategy?.gameStrategy ? 'completed' : 'optional',
        priority: 'medium',
        section: 'coach'
      }
    ];

    return tasks;
  };

  const getTaskIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-[hsl(var(--section-completed))]" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-[hsl(var(--action-required))]" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />;
    }
  };

  const getTaskBadge = (status: string, priority: string) => {
    if (status === 'completed') {
      return <Badge className="action-completed">Completed</Badge>;
    }
    if (status === 'pending' && priority === 'high') {
      return <Badge className="action-required">Required</Badge>;
    }
    return <Badge variant="outline">Optional</Badge>;
  };

  const tasks = getCoachTasks();
  const completedTasks = tasks.filter(t => t.status === 'completed').length;
  const totalTasks = tasks.filter(t => t.status !== 'optional').length;

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Coach Dashboard
          <Badge variant="outline">{userTeamInfo.teamSide?.toUpperCase()} Team</Badge>
        </CardTitle>
        <CardDescription>
          Your coaching responsibilities for this game
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Progress Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-[hsl(var(--section-completed))]">
              {completedTasks}
            </div>
            <div className="text-sm text-muted-foreground">Tasks Completed</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-[hsl(var(--action-required))]">
              {totalTasks - completedTasks}
            </div>
            <div className="text-sm text-muted-foreground">Tasks Remaining</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-[hsl(var(--primary))]">
              {Math.round((completedTasks / totalTasks) * 100) || 0}%
            </div>
            <div className="text-sm text-muted-foreground">Progress</div>
          </div>
        </div>

        <Separator />

        {/* Task List */}
        <div className="space-y-3">
          <h4 className="font-semibold flex items-center gap-2">
            <Clipboard className="h-4 w-4" />
            Your Action Items
          </h4>
          
          {tasks.map((task) => (
            <div
              key={task.id}
              className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
            >
              <div className="flex items-start gap-3">
                {getTaskIcon(task.status)}
                <div>
                  <h5 className="font-medium">{task.title}</h5>
                  <p className="text-sm text-muted-foreground">{task.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {getTaskBadge(task.status, task.priority)}
                {task.status !== 'completed' && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onSectionFocus(task.section)}
                  >
                    {task.status === 'pending' ? 'Complete' : 'Edit'}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="space-y-3">
          <h4 className="font-semibold flex items-center gap-2">
            <Target className="h-4 w-4" />
            Quick Actions
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onSectionFocus('coach')}
              className="justify-start"
            >
              <Users className="h-4 w-4 mr-2" />
              Manage Team Roster
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onSectionFocus('player')}
              className="justify-start"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Check Player Status
            </Button>
          </div>
        </div>

        {/* Game Info Summary */}
        <div className="bg-muted/50 p-4 rounded-lg">
          <h5 className="font-medium mb-2">Game Summary</h5>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Date:</span>
              <div className="font-medium">
                {(() => {
                  const formatted = formatGameDateTime(
                    gameData.game_date, 
                    gameData.game_time, 
                    gameData.game_timezone || 'UTC'
                  );
                  return formatted.date;
                })()}
              </div>
            </div>
            <div>
              <span className="text-muted-foreground">Time:</span>
              <div className="font-medium">
                {(() => {
                  const formatted = formatGameDateTime(
                    gameData.game_date, 
                    gameData.game_time, 
                    gameData.game_timezone || 'UTC'
                  );
                  return formatted.timeWithZone;
                })()}
              </div>
            </div>
            <div>
              <span className="text-muted-foreground">Venue:</span>
              <div className="font-medium">{gameData.venue_name || 'TBD'}</div>
            </div>
            <div>
              <span className="text-muted-foreground">Your Team:</span>
              <div className="font-medium capitalize">{userTeamInfo.teamSide} Team</div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};